import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import GalleryLightbox from '../components/GalleryLightbox';
import { imageUrl } from '../lib/images';

export default function BatteriboksePage() {
  const databladPdf = 'https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/joca-produktblad-batteriboks-rb.pdf';
  const vedligeholdPdf = 'https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/vedligehold-overflader.pdf';

  return (
    <>
      <SeoHead
        title="Batteribokse - RB Glasfiber A/S"
        description="Download datablad. Batteribokse - RB 10 og RB 30. Batteriboks i samme design som igloen, fremstillet i glasfiber med kraftig gummiindkast. Tømningen er enkel gennem bundklap."
        canonical="https://www.rb-glasfiber.dk/produkter/batteribokse"
        ogImage="https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/batteriboks-resizewidth-625.webp"
      />

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 bg-slate-950 text-white overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/batteriboks-resizewidth-625.webp', 'width=1600,quality=80,format=auto')}
            alt="Batteribokse RB 10 og RB 30"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#1e9457]/20 border border-[#1e9457]/40 text-[#22c55e] text-xs font-bold uppercase tracking-widest mb-4">
              <i className="fa-solid fa-battery-full"></i>
              <span>Miljø & Batteriindsamling</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white mb-4">
              Batteribokse
            </h1>
            <p className="text-xl text-[#22c55e] font-semibold mb-6">
              RB 10 og RB 30
            </p>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Batteriboks i samme design som igloen, fremstillet i glasfiber med kraftig gummiindkast. Tømningen er enkel, det sker gennem klappen i bunden, som skal låses med en nøgle på forsiden af boksen.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={databladPdf}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded bg-[#1e9457] hover:bg-[#15703f] text-white font-bold uppercase tracking-wider text-sm shadow transition-all"
              >
                <i className="fa-solid fa-file-pdf"></i>
                <span>Download datablad (PDF)</span>
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm border border-slate-700 transition-all"
              >
                <span>Bestil eller forespørg</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-[#1e9457] uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-[#1e9457]"></span>
                Ideel til genbrugsmiljøer
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 uppercase">
                Sikker indsamling af batterier og småelektronik
              </h2>
              <div className="prose text-slate-700 text-base leading-relaxed space-y-4">
                <p>
                  Batteriboksen passer godt ind i genbrugsmilijøet, hvor der er igloer f.eks til pap/papir og glas. Batteriboksen kan monteres direkte på igloen eller på en stander/væg.
                </p>
                <p>
                  Batteriboksen er udført i robust, vejrbestandig glasfiber med flot glatpoleret finish og lang holdbarhed i det danske klima.
                </p>
              </div>

              {/* Teknisk kendetegn */}
              <div className="p-6 rounded-lg bg-slate-50 border border-slate-200">
                <h3 className="font-bold text-slate-900 uppercase text-sm mb-3">Teknisk kendetegn</h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm text-slate-700">
                  <li className="flex items-center gap-2"><i className="fa-solid fa-check text-[#1e9457]"></i><span>Fremstillet i glasfiber</span></li>
                  <li className="flex items-center gap-2"><i className="fa-solid fa-check text-[#1e9457]"></i><span>Størrelse: 10 og 30 l</span></li>
                  <li className="flex items-center gap-2"><i className="fa-solid fa-check text-[#1e9457]"></i><span>Indkast i kraftig gummi</span></li>
                  <li className="flex items-center gap-2"><i className="fa-solid fa-check text-[#1e9457]"></i><span>Tømning gennem bunden</span></li>
                  <li className="flex items-center gap-2"><i className="fa-solid fa-check text-[#1e9457]"></i><span>Farve efter ønske</span></li>
                  <li className="flex items-center gap-2"><i className="fa-solid fa-check text-[#1e9457]"></i><span>RB 30 med 2 indkaststørrelser</span></li>
                  <li className="flex items-center gap-2"><i className="fa-solid fa-check text-[#1e9457]"></i><span>Glatpoleret overflade i flot finish</span></li>
                  <li className="flex items-center gap-2"><i className="fa-solid fa-check text-[#1e9457]"></i><span>Mulighed for individuel profilering</span></li>
                </ul>
              </div>

              {/* Mål specifikationer fra datablad */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded border border-slate-200 bg-slate-50">
                  <div className="font-bold text-slate-900 text-sm mb-2">Mål RB 10:</div>
                  <div className="text-xs text-slate-600 space-y-1 font-mono">
                    <div>Diameter: <strong className="text-slate-900">340 mm</strong></div>
                    <div>Højde: <strong className="text-slate-900">280 mm</strong></div>
                    <div>Indkast diameter: <strong className="text-slate-900">65 mm</strong></div>
                    <div>Højde til indkast midte: <strong className="text-slate-900">230 mm</strong></div>
                    <div>Volumen: <strong className="text-slate-900">ca. 10 liter</strong></div>
                  </div>
                </div>

                <div className="p-4 rounded border border-slate-200 bg-slate-50">
                  <div className="font-bold text-slate-900 text-sm mb-2">Mål RB 30:</div>
                  <div className="text-xs text-slate-600 space-y-1 font-mono">
                    <div>Diameter: <strong className="text-slate-900">290 mm</strong></div>
                    <div>Højde: <strong className="text-slate-900">480 mm</strong></div>
                    <div>Indkast diameter: <strong className="text-slate-900">120 mm</strong></div>
                    <div>Højde til indkast midte: <strong className="text-slate-900">370 mm</strong></div>
                    <div>Volumen: <strong className="text-slate-900">ca. 30 liter</strong></div>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <a
                  href={vedligeholdPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-[#1e9457]"
                >
                  <i className="fa-solid fa-file-pdf"></i>
                  <span>Læs vejledning i vedligehold af gel-coat overflader (PDF)</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-md aspect-[3/4] bg-slate-900 max-w-sm mx-auto">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/batteriboks-resizewidth-625.webp', 'width=600,quality=85,format=auto')}
                  alt="Batteriboks RB 10 og RB 30"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Lightbox Gallery */}
          <div className="pt-8 border-t border-slate-200">
            <GalleryLightbox
              gallerySlug="standardprodukter"
              title="Fotogalleri: Standard produkter & genbrugsløsninger"
              description="Billeder af batteribokse og affaldsløsninger."
              columns={3}
            />
          </div>
        </div>
      </section>
    </>
  );
}
